/* eslint-disable react/require-default-props */
import React, { useState, useEffect } from 'react';
import classNames from 'classnames';
import './AvatarStyles.scss';
const PLACEHOLDER_ICON_CLASS = {
    technician: 'fa-light fa-user-helmet-safety',
    subcontractor: 'fa-light fa-handshake'
};
/**
 * Derive up to 2 uppercase initials from a label.
 * - "Enmanuel Carrasquel" → "EC"
 * - "John Michael Smith"  → "JS" (first + last)
 * - "Enmanuel"            → "E"
 * - ""                    → ""
 */
export function getInitials(label) {
    if (!label)
        return '';
    const words = label
        .trim()
        .split(/\s+/)
        .filter((w) => w.length > 0);
    if (words.length === 0)
        return '';
    if (words.length === 1)
        return (words[0][0] || '').toUpperCase();
    const first = words[0][0] || '';
    const last = words[words.length - 1][0] || '';
    return (first + last).toUpperCase();
}
/**
 * sRGB → linear RGB per the WCAG 2.x relative luminance definition.
 * https://www.w3.org/WAI/GL/wiki/Relative_luminance
 */
function srgbToLinear(channel) {
    const c = channel / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}
/**
 * Return '#ffffff' or '#000000' depending on which gives better WCAG contrast
 * against the provided background hex — ensures readable initials over the
 * entity background color. Threshold 0.179 is the WCAG crossover where black
 * and white yield equal contrast (~4.5:1, AA for normal text); below that
 * luminance white wins, above it black wins.
 */
export function getContrastTextColor(hexColor) {
    if (!hexColor)
        return '#ffffff';
    let hex = hexColor.replace('#', '').trim();
    if (hex.length === 3) {
        hex = hex
            .split('')
            .map((c) => c + c)
            .join('');
    }
    if (hex.length !== 6)
        return '#ffffff';
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b))
        return '#ffffff';
    const luminance = 0.2126 * srgbToLinear(r) + 0.7152 * srgbToLinear(g) + 0.0722 * srgbToLinear(b);
    return luminance < 0.179 ? '#ffffff' : '#000000';
}
/**
 * Avatar component — visually represents a person, entity, or assignment state.
 *
 * Precedence:
 * 1. `src` → image variant (falls back to initials on load error)
 * 2. `label` → SVG initials (up to 2 letters) with entity color background
 * 3. placeholder variant (light/dark) with icon
 *
 * @example
 * <Avatar label="Enmanuel Carrasquel" color="#5484ed" size="48" />
 * <Avatar src="/avatar.png" label="Enmanuel Carrasquel" color="#5484ed" />
 * <Avatar size="32" placeholderVariant="dark" placeholderIcon="subcontractor" />
 */
export function Avatar({ label, src, color, size = '48', placeholderVariant = 'light', placeholderIcon = 'technician', action, className }) {
    const [imageFailed, setImageFailed] = useState(false);
    useEffect(() => {
        setImageFailed(false);
    }, [src]);
    const initials = getInitials(label);
    const textColor = getContrastTextColor(color);
    const sizeClass = `qmb-ui-avatar--${size}`;
    const showImage = Boolean(src) && !imageFailed;
    // Subcontractor entities always render as the icon-placeholder variant — they
    // are organizations, not people, so deriving initials from a tenant/company
    // name produces meaningless letters. The handshake icon is the canonical
    // representation.
    const isSubcontractorPlaceholder = placeholderIcon === 'subcontractor';
    const showInitials = !showImage && !isSubcontractorPlaceholder && initials.length > 0 && Boolean(color);
    const rootClasses = classNames('qmb-ui-avatar', sizeClass, className);
    if (showImage) {
        return (React.createElement("span", { className: rootClasses },
            React.createElement("img", { alt: (label === null || label === void 0 ? void 0 : label.trim()) || initials || 'Avatar', className: "qmb-ui-avatar__image", style: color ? { borderColor: color } : undefined, src: src, onError: () => setImageFailed(true) }),
            action && React.createElement("span", { className: "qmb-ui-avatar__action" }, action)));
    }
    if (showInitials) {
        return (React.createElement("span", { className: rootClasses },
            React.createElement("svg", { viewBox: "0 0 16 16", "aria-hidden": "true", focusable: "false" },
                React.createElement("circle", { cx: "8", cy: "8", r: "8", style: { fill: color } }),
                React.createElement("text", { x: "50%", y: "55%", dominantBaseline: "middle", textAnchor: "middle", style: { fill: textColor } }, initials)),
            React.createElement("span", { className: "qmb-ui-avatar__sr" }, label),
            action && React.createElement("span", { className: "qmb-ui-avatar__action" }, action)));
    }
    // Placeholder variant. When a `color` is provided, tint the placeholder with
    // that color and pick a contrast-safe foreground for the icon — used for
    // entity placeholders (e.g. subcontractors) that still want a brand color.
    const placeholderClass = placeholderVariant === 'dark' ? 'qmb-ui-avatar--placeholder-dark' : 'qmb-ui-avatar--placeholder-light';
    const placeholderStyle = color ? { background: color, color: textColor } : undefined;
    return (React.createElement("span", { className: classNames(rootClasses, placeholderClass), role: "img", "aria-label": label || 'Unassigned', style: placeholderStyle },
        React.createElement("i", { className: PLACEHOLDER_ICON_CLASS[placeholderIcon], "aria-hidden": "true" }),
        action && React.createElement("span", { className: "qmb-ui-avatar__action" }, action)));
}
